/**
 * @typedef {import('../../../types/AuthContext').default} Auth
 * @typedef {import('../../shared/PropertyService/EditablePropertyDetails').default} EditablePropertyDetails
 * @typedef {import('./MortgageInformationCardProps').default} Props
 * @typedef {import('./MortgageInformationCardState').default} State
 * @typedef {{ amountToLoan: string, amortizationYears: string, escrowInsurance: string, escrowInsuranceFrequency: 'annual' | 'monthly', escrowTax: string, escrowTaxFrequency: 'annual' | 'monthly', originalDateOfContract: string, rate: string }} MortgageDraft
 * @typedef {Partial<Omit<NonNullable<EditablePropertyDetails['details']>, 'originalDateOfContract'>> & { originalDateOfContract?: Date | null }} ParsedMortgageDetails
 */

import { translate } from 'App/Internationalization';
import { withProperty } from '../PropertyProvider';
import { withPropertyService } from '../../../service-container';
import { AuthContext } from '../../../shared/AuthProvider';
import * as homeownerPropertyService from '../Properties/property-service';
import BaseButton from '../../../shared/BaseButton/BaseButton';
import captureError from '../../../utils/captureError';
import {
	calculateEstimatedCurrentBalance,
	calculateEstimatedCurrentEquity,
	calculateFinanceWidgetMonthlyPayment,
	getLatestQuantariumValueInDollars,
	parseFinanceWidgetDate,
	resolveFinanceWidgetOriginationDate,
} from '../../shared/FinanceWidgets/financeWidgetAttributes';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import Loading from '../../../shared/Loading';
import React from 'react';

/** @extends {React.Component<Props, State>} */
export class MortgageInformationCard extends React.Component {
	/** @type {Auth} */
	context = /** @type {Auth} */ ({});

	/** @type {string | null} */
	#activeMortgagePropertyId = null;

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			error: false,
			errorMessage: null,
			isEditing: false,
			isGettingData: true,
			isSaving: false,
			mortgageDraft: null,
			priceHistory: null,
			propertyDetails: null,
			valuations: null,
		};

		this.handleCancelEdit = this.handleCancelEdit.bind(this);
		this.handleDraftChange = this.handleDraftChange.bind(this);
		this.handleSave = this.handleSave.bind(this);
		this.handleStartEdit = this.handleStartEdit.bind(this);
		this.getMortgageInfo = this.getMortgageInfo.bind(this);
	}

	componentDidMount() {
		this.getMortgageInfo();
	}

	/**
	 * @param {Props} prevProps
	 * @returns {void}
	 */
	componentDidUpdate(prevProps) {
		const prevPropertyId =
			prevProps.match?.params?.propertyId ??
			prevProps.property?.selectedProperty?.id;

		if (this.#getPropertyId() === prevPropertyId) {
			return;
		}

		this.setState({
			error: false,
			errorMessage: null,
			isEditing: false,
			isGettingData: true,
			isSaving: false,
			mortgageDraft: null,
			priceHistory: null,
			propertyDetails: null,
			valuations: null,
		});

		this.getMortgageInfo();
	}

	/** @returns {string | undefined} */
	#getPropertyId() {
		return (
			this.props.match?.params?.propertyId ??
			this.props.property?.selectedProperty?.id
		);
	}

	/**
	 * @param {string | undefined} propertyId
	 * @returns {boolean}
	 */
	#isActiveMortgageRequest(propertyId) {
		return this.#activeMortgagePropertyId === (propertyId ?? null);
	}

	async getMortgageInfo() {
		/** @type {string | undefined} */
		let propertyId;

		try {
			propertyId = this.#getPropertyId();
			this.#activeMortgagePropertyId = propertyId ?? null;

			if (!propertyId) {
				throw new Error(
					translate(
						'homeowner.pages.dashboard.properties.property_info_error'
					)
				);
			}

			const [mortgageInfo, valuations, priceHistory] = await Promise.all([
				this.props.propertyService.getEditablePropertyById(propertyId),
				this.props.propertyService
					.getValuations(propertyId)
					.catch(() => null),
				this.props.propertyService
					.getPriceHistory(propertyId)
					.catch(() => null),
			]);

			if (!this.#isActiveMortgageRequest(propertyId)) {
				return;
			}

			this.setState({
				isGettingData: false,
				priceHistory,
				propertyDetails: mortgageInfo,
				valuations,
			});
		} catch (error) {
			captureError(error);

			if (!this.#isActiveMortgageRequest(propertyId)) {
				return;
			}

			this.setState({
				error: true,
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageCurrency(value) {
		return value === null || value === undefined
			? ''
			: value.toLocaleString('en-US', {
					currency: 'USD',
					maximumFractionDigits: 0,
					style: 'currency',
			  });
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {Date | null}
	 */
	static #normalizeMortgageDate(value) {
		return parseFinanceWidgetDate(value);
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageDisplayDate(value) {
		const date = MortgageInformationCard.#normalizeMortgageDate(value);

		return date ? date.toLocaleDateString('en-US') : '';
	}

	/**
	 * @param {Date | string | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageDateForInput(value) {
		const date = MortgageInformationCard.#normalizeMortgageDate(value);

		if (!date) {
			return '';
		}

		return [
			date.getFullYear(),
			String(date.getMonth() + 1).padStart(2, '0'),
			String(date.getDate()).padStart(2, '0'),
		].join('-');
	}

	/**
	 * @param {string} value
	 * @returns {Date | null}
	 */
	static #parseMortgageDateInput(value) {
		if (!value) {
			return null;
		}

		const dateParts = value.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);

		if (dateParts) {
			const [, month, day, year] = dateParts;
			const parsedMonth = Number(month);
			const parsedDay = Number(day);
			const parsedYear = Number(year);
			const date = new Date(parsedYear, parsedMonth - 1, parsedDay);

			if (
				Number.isNaN(date.valueOf()) ||
				date.getFullYear() !== parsedYear ||
				date.getMonth() !== parsedMonth - 1 ||
				date.getDate() !== parsedDay
			) {
				return null;
			}

			return date;
		}

		const date = new Date(`${value}T00:00:00`);

		return Number.isNaN(date.valueOf()) ? null : date;
	}

	/**
	 * @param {Date | null} value
	 * @returns {string | null}
	 */
	static #formatMortgageDateForSaving(value) {
		return value
			? MortgageInformationCard.#formatMortgageDateForInput(value)
			: null;
	}

	/**
	 * @param {string} value
	 * @returns {number | null}
	 */
	static #parseMortgageNumberInput(value) {
		if (value === '') {
			return null;
		}

		const number = Number(value);

		return Number.isNaN(number) ? null : number;
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {string}
	 */
	static #formatMortgageNumberInput(value) {
		return value === null || value === undefined ? '' : String(value);
	}

	/**
	 * @param {number} value
	 * @returns {number}
	 */
	static #roundMortgageAmount(value) {
		return Math.round((value + Number.EPSILON) * 100) / 100;
	}

	/**
	 * @returns {'annual' | 'monthly'}
	 */
	static #getDefaultEscrowFrequency() {
		return 'monthly';
	}

	/**
	 * @param {number | null | undefined} amount
	 * @param {number | null | undefined} monthlyAmount
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {string}
	 */
	static #formatEscrowDraftAmount(amount, monthlyAmount, frequency) {
		if (amount !== null && amount !== undefined) {
			return MortgageInformationCard.#formatMortgageNumberInput(amount);
		}

		if (monthlyAmount === null || monthlyAmount === undefined) {
			return '';
		}

		const displayAmount =
			frequency === 'annual' ? monthlyAmount * 12 : monthlyAmount;

		return MortgageInformationCard.#formatMortgageNumberInput(
			MortgageInformationCard.#roundMortgageAmount(displayAmount)
		);
	}

	/**
	 * @param {number | null | undefined} value
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {number | null}
	 */
	static #normalizeEscrowMonthlyAmount(value, frequency) {
		if (value === null || value === undefined) {
			return null;
		}

		const monthlyValue = frequency === 'annual' ? value / 12 : value;

		return MortgageInformationCard.#roundMortgageAmount(monthlyValue);
	}

	/**
	 * @param {NonNullable<import('../../shared/PropertyService/EditablePropertyDetails').default['details']>} details
	 * @returns {MortgageDraft}
	 */
	static #createMortgageDraft(details) {
		const escrowInsuranceFrequency =
			details.escrowInsuranceFrequency ??
			MortgageInformationCard.#getDefaultEscrowFrequency();
		const escrowTaxFrequency =
			details.escrowTaxFrequency ??
			MortgageInformationCard.#getDefaultEscrowFrequency();

		return {
			amortizationYears:
				MortgageInformationCard.#formatMortgageNumberInput(
					details.amortizationYears ?? 30
				),
			amountToLoan: MortgageInformationCard.#formatMortgageNumberInput(
				details.amountToLoan
			),
			escrowInsurance: MortgageInformationCard.#formatEscrowDraftAmount(
				details.escrowInsuranceAmount,
				details.escrowInsuranceMonthly,
				escrowInsuranceFrequency
			),
			escrowInsuranceFrequency,
			escrowTax: MortgageInformationCard.#formatEscrowDraftAmount(
				details.escrowTaxAmount,
				details.escrowTaxMonthly,
				escrowTaxFrequency
			),
			escrowTaxFrequency,
			originalDateOfContract:
				MortgageInformationCard.#formatMortgageDateForInput(
					details.originalDateOfContract
				),
			rate: MortgageInformationCard.#formatMortgageNumberInput(
				details.rate
			),
		};
	}

	/**
	 * @param {MortgageDraft} draft
	 * @returns {ParsedMortgageDetails}
	 */
	static #mapMortgageDraftToDetails(draft) {
		const escrowInsurance =
			MortgageInformationCard.#parseMortgageNumberInput(
				draft.escrowInsurance
			);
		const escrowTax = MortgageInformationCard.#parseMortgageNumberInput(
			draft.escrowTax
		);

		return {
			amortizationYears:
				MortgageInformationCard.#parseMortgageNumberInput(
					draft.amortizationYears
				),
			amountToLoan: MortgageInformationCard.#parseMortgageNumberInput(
				draft.amountToLoan
			),
			escrowInsuranceAmount:
				escrowInsurance === null
					? null
					: MortgageInformationCard.#roundMortgageAmount(
							escrowInsurance
					  ),
			escrowInsuranceFrequency: draft.escrowInsuranceFrequency,
			escrowInsuranceMonthly:
				MortgageInformationCard.#normalizeEscrowMonthlyAmount(
					escrowInsurance,
					draft.escrowInsuranceFrequency
				),
			escrowTaxAmount:
				escrowTax === null
					? null
					: MortgageInformationCard.#roundMortgageAmount(escrowTax),
			escrowTaxFrequency: draft.escrowTaxFrequency,
			escrowTaxMonthly:
				MortgageInformationCard.#normalizeEscrowMonthlyAmount(
					escrowTax,
					draft.escrowTaxFrequency
				),
			originalDateOfContract:
				MortgageInformationCard.#parseMortgageDateInput(
					draft.originalDateOfContract
				),
			rate: MortgageInformationCard.#parseMortgageNumberInput(draft.rate),
		};
	}

	/** @returns {void} */
	handleStartEdit() {
		const details = this.state.propertyDetails?.details;

		if (!details) {
			return;
		}

		this.setState({
			errorMessage: null,
			isEditing: true,
			mortgageDraft:
				MortgageInformationCard.#createMortgageDraft(details),
		});
	}

	/** @returns {void} */
	handleCancelEdit() {
		this.setState({
			errorMessage: null,
			isEditing: false,
			mortgageDraft: null,
		});
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} event
	 * @returns {void}
	 */
	handleDraftChange(event) {
		const { name, value } = event.currentTarget;

		this.setState((prevState) => ({
			mortgageDraft: prevState.mortgageDraft
				? {
						...prevState.mortgageDraft,
						[name]: value,
				  }
				: null,
		}));
	}

	/** @returns {Promise<void>} */
	async handleSave() {
		const accessToken = this.context?.accessToken;
		const propertyId = this.#getPropertyId();
		const propertyDetails = this.state.propertyDetails;
		const mortgageDraft = this.state.mortgageDraft;

		if (
			!accessToken ||
			!propertyId ||
			!propertyDetails?.details ||
			!mortgageDraft
		) {
			this.setState({ errorMessage: translate('global.error') });
			return;
		}

		const mortgageDetails =
			MortgageInformationCard.#mapMortgageDraftToDetails(mortgageDraft);
		const nextMortgageDetails = {
			...mortgageDetails,
			originalDateOfContract:
				MortgageInformationCard.#formatMortgageDateForSaving(
					mortgageDetails.originalDateOfContract ?? null
				),
		};
		/** @type {Partial<NonNullable<import('../../shared/PropertyService/EditablePropertyDetails').default['details']>>} */
		const mortgageDetailsForSaving = nextMortgageDetails;
		const nextPropertyDetails = {
			...propertyDetails,
			details: {
				...propertyDetails.details,
				...mortgageDetailsForSaving,
			},
		};

		this.setState({ errorMessage: null, isSaving: true });

		try {
			await homeownerPropertyService.updatePropertyMortgageDetails(
				propertyId,
				mortgageDetailsForSaving,
				accessToken
			);

			this.setState({
				isEditing: false,
				isSaving: false,
				mortgageDraft: null,
				propertyDetails: nextPropertyDetails,
			});
			this.props.onMortgageSaved?.(propertyId, nextMortgageDetails);
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isSaving: false,
			});
		}
	}

	/**
	 * @param {string} header
	 * @param {string | number | null | undefined} footer
	 * @returns {JSX.Element}
	 */
	static #renderMortgageItem(header, footer) {
		return (
			<div className="property-detail-item" key={header}>
				<dt className="property-detail-item-header">{header}</dt>
				<dd className="property-detail-item-footer">
					{footer ||
						translate(
							'homeowner.pages.dashboard.properties.missing_datum'
						)}
				</dd>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string} name
	 * @param {string} value
	 * @param {'date' | 'number'} type
	 * @param {string} [suffix]
	 * @returns {JSX.Element}
	 */
	#renderMortgageFormField(header, name, value, type, suffix = '') {
		return (
			<div className="mortgage-form-field min-w-0" key={header}>
				<label
					className="mortgage-form-label block h-ms mb-2 text-grey-900"
					htmlFor={name}
				>
					{header}
				</label>
				<div className="mortgage-form-control flex items-center gap-2 min-w-0">
					<input
						className="input mortgage-form-input min-w-0 w-full"
						id={name}
						inputMode={type === 'number' ? 'decimal' : undefined}
						min={type === 'number' ? '0' : undefined}
						name={name}
						onChange={this.handleDraftChange}
						step={type === 'number' ? '0.01' : undefined}
						type={type === 'number' ? 'text' : type}
						value={value}
					/>
					{suffix ? (
						<span className="mortgage-form-suffix hl-ms whitespace-nowrap">
							{suffix}
						</span>
					) : null}
				</div>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string} name
	 * @param {string} value
	 * @param {'annual' | 'monthly'} frequency
	 * @returns {JSX.Element}
	 */
	#renderMortgageEscrowField(header, name, value, frequency) {
		return (
			<div
				className="mortgage-form-field mortgage-form-field-compact min-w-0"
				key={header}
			>
				<label
					className="mortgage-form-label block h-ms mb-2 text-grey-900"
					htmlFor={name}
				>
					{header}
				</label>
				<div className="mortgage-form-control flex items-center gap-2 min-w-0">
					<input
						className="input mortgage-form-input min-w-0 w-full"
						id={name}
						inputMode="decimal"
						min="0"
						name={name}
						onChange={this.handleDraftChange}
						step="0.01"
						type="text"
						value={value}
					/>
					<select
						className="input mortgage-form-select min-w-0 w-36"
						name={`${name}Frequency`}
						onChange={this.handleDraftChange}
						value={frequency}
					>
						<option value="monthly">{'Monthly'}</option>
						<option value="annual">{'Annual'}</option>
					</select>
				</div>
			</div>
		);
	}

	/**
	 * @param {string} header
	 * @param {string | null} value
	 * @returns {JSX.Element}
	 */
	static #renderMortgageDisplayField(header, value) {
		return (
			<div className="mortgage-form-field min-w-0" key={header}>
				<div className="mortgage-form-label block h-ms mb-2 text-grey-900">
					{header}
				</div>
				<div className="mortgage-form-readonly min-h-10 border border-grey-100 rounded px-3 py-2 hl-ms">
					{value ||
						translate(
							'homeowner.pages.dashboard.properties.missing_datum'
						)}
				</div>
			</div>
		);
	}

	/**
	 * @param {{amortizationPeriod: string, amountToLoan: string, escrowInsurance: string, escrowTax: string, estimatedPrincipalInterest: string, interestRate: string, originalDateOfContract: string, totalPayment: string, years: string}} labels
	 * @param {string | null} estimatedPrincipalAndInterest
	 * @param {string | null} totalPayment
	 * @returns {JSX.Element | null}
	 */
	#renderMortgageEditForm(
		labels,
		estimatedPrincipalAndInterest,
		totalPayment
	) {
		/** @type {MortgageDraft | null} */
		const draft = this.state.mortgageDraft;

		if (!draft) {
			return null;
		}

		return (
			<div className="card-body mortgage-edit-form grid gap-x-8 gap-y-5 text-left md:grid-cols-2">
				{this.#renderMortgageFormField(
					labels.amountToLoan,
					'amountToLoan',
					draft.amountToLoan,
					'number'
				)}
				{this.#renderMortgageFormField(
					labels.originalDateOfContract,
					'originalDateOfContract',
					draft.originalDateOfContract,
					'date'
				)}
				{this.#renderMortgageFormField(
					labels.interestRate,
					'rate',
					draft.rate,
					'number',
					'%'
				)}
				{this.#renderMortgageFormField(
					labels.amortizationPeriod,
					'amortizationYears',
					draft.amortizationYears,
					'number',
					labels.years
				)}
				{MortgageInformationCard.#renderMortgageDisplayField(
					labels.estimatedPrincipalInterest,
					estimatedPrincipalAndInterest
				)}
				{this.#renderMortgageEscrowField(
					labels.escrowTax,
					'escrowTax',
					draft.escrowTax,
					draft.escrowTaxFrequency
				)}
				{this.#renderMortgageEscrowField(
					labels.escrowInsurance,
					'escrowInsurance',
					draft.escrowInsurance,
					draft.escrowInsuranceFrequency
				)}
				{MortgageInformationCard.#renderMortgageDisplayField(
					labels.totalPayment,
					totalPayment
				)}
			</div>
		);
	}

	/** @returns {JSX.Element | null} */
	// eslint-disable-next-line complexity -- assembles the read/edit mortgage card model
	#renderMortgageInformation() {
		/** @type {Partial<NonNullable<import('../../shared/PropertyService/EditablePropertyDetails').default['details']>>} */
		const details = this.state.propertyDetails?.details ?? {};
		const mortgageDetails =
			this.state.isEditing && this.state.mortgageDraft
				? {
						...details,
						...MortgageInformationCard.#mapMortgageDraftToDetails(
							this.state.mortgageDraft
						),
				  }
				: details;
		const originationDate = resolveFinanceWidgetOriginationDate(
			mortgageDetails.originalDateOfContract,
			this.state.priceHistory
		);
		const calculationMortgageDetails = {
			...mortgageDetails,
			originalDateOfContract: originationDate,
		};
		const amortizationYears = mortgageDetails.amortizationYears ?? 30;
		const estimatedPrincipalAndInterest =
			calculateFinanceWidgetMonthlyPayment(
				mortgageDetails.amountToLoan,
				mortgageDetails.rate,
				amortizationYears
			);
		const escrowTaxMonthly = mortgageDetails.escrowTaxMonthly ?? 0;
		const escrowInsuranceMonthly =
			mortgageDetails.escrowInsuranceMonthly ?? 0;
		const totalPayment =
			estimatedPrincipalAndInterest === null
				? null
				: estimatedPrincipalAndInterest +
				  escrowTaxMonthly +
				  escrowInsuranceMonthly;
		const estimatedCurrentBalance = calculateEstimatedCurrentBalance(
			calculationMortgageDetails
		);
		const valuations =
			/** @type {Map<string, { valueMean?: number | null, date?: Date | null }[]> | null} */ (
				this.state.valuations
			);
		const estimatedCurrentEquity = calculateEstimatedCurrentEquity(
			getLatestQuantariumValueInDollars(valuations),
			estimatedCurrentBalance
		);
		const noContent = translate(
			'homeowner.pages.dashboard.properties.missing_datum'
		);
		const missingRequiredData = 'Missing Required Data';
		const labels = {
			amortizationPeriod: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.amortization_period'
			),
			escrowInsurance: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.escrow_insurance'
			),
			escrowTax: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.escrow_tax'
			),
			estimatedCurrentBalance: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_current_balance'
			),
			estimatedCurrentEquity: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_current_equity'
			),
			estimatedPrincipalInterest: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.estimated_principal_interest'
			),
			interestRate: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.interest_rate'
			),
			originalAmount: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.original_amount'
			),
			originationDate: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.origination_date'
			),
			totalPayment: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.total_payment'
			),
			years: translate(
				'agent.pages.contacts.edit.finances_valuation.mortgage.years'
			),
		};
		const items = [
			{
				footer: MortgageInformationCard.#formatMortgageCurrency(
					mortgageDetails.amountToLoan
				),
				header: labels.originalAmount,
			},
			{
				footer:
					MortgageInformationCard.#formatMortgageDisplayDate(
						originationDate
					) || noContent,
				header: labels.originationDate,
			},
			{
				footer:
					originationDate === null
						? missingRequiredData
						: MortgageInformationCard.#formatMortgageCurrency(
								estimatedCurrentBalance
						  ),
				header: labels.estimatedCurrentBalance,
			},
			{
				footer:
					mortgageDetails.rate === null
						? null
						: `${mortgageDetails.rate}%`,
				header: labels.interestRate,
			},
			{
				footer:
					originationDate === null
						? missingRequiredData
						: MortgageInformationCard.#formatMortgageCurrency(
								estimatedCurrentEquity
						  ),
				header: labels.estimatedCurrentEquity,
			},
			{
				footer: `${amortizationYears} ${labels.years}`,
				header: labels.amortizationPeriod,
			},
			{
				footer: MortgageInformationCard.#formatMortgageCurrency(
					estimatedPrincipalAndInterest
				),
				header: labels.estimatedPrincipalInterest,
			},
			{
				footer: MortgageInformationCard.#formatMortgageCurrency(
					mortgageDetails.escrowTaxMonthly
				),
				header: labels.escrowTax,
			},
			{
				footer: MortgageInformationCard.#formatMortgageCurrency(
					mortgageDetails.escrowInsuranceMonthly
				),
				header: labels.escrowInsurance,
			},
			{
				footer: MortgageInformationCard.#formatMortgageCurrency(
					totalPayment
				),
				header: labels.totalPayment,
			},
		];

		return this.state.isEditing ? (
			this.#renderMortgageEditForm(
				{
					...labels,
					amountToLoan: labels.originalAmount,
					originalDateOfContract: labels.originationDate,
				},
				MortgageInformationCard.#formatMortgageCurrency(
					estimatedPrincipalAndInterest
				) || noContent,
				MortgageInformationCard.#formatMortgageCurrency(totalPayment)
			)
		) : (
			<dl className="card-body property-details grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-start">
				{items.map((item) =>
					MortgageInformationCard.#renderMortgageItem(
						item.header,
						item.footer
					)
				)}
			</dl>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	render() {
		if (this.state.error) {
			return null;
		}

		return (
			<section className="mt-10">
				<header className="mb-3 flex items-center justify-between gap-3">
					<h2 className="h-ms-4">
						{translate(
							'homeowner.pages.finances.view.mortgage.title'
						)}
					</h2>
					{this.state.isEditing ? (
						<div className="flex gap-2">
							<BaseButton
								disabled={this.state.isSaving}
								onClick={this.handleSave}
								size="small"
							>
								{this.state.isSaving
									? 'Saving...'
									: translate(
											'global.forms.buttons.save_text'
									  )}
							</BaseButton>
							<BaseButton
								disabled={this.state.isSaving}
								onClick={this.handleCancelEdit}
								outline={true}
								size="small"
							>
								{translate('global.forms.buttons.cancel')}
							</BaseButton>
						</div>
					) : (
						<BaseButton onClick={this.handleStartEdit} size="small">
							{translate(
								'homeowner.pages.finances.view.mortgage.edit_details'
							)}
						</BaseButton>
					)}
				</header>
				{this.state.errorMessage ? (
					<FormErrorMessages messages={this.state.errorMessage} />
				) : null}
				<section className="card card-property-detail card-border">
					{this.state.isGettingData ? (
						<div className="card-body">
							<Loading />
						</div>
					) : (
						this.#renderMortgageInformation()
					)}
				</section>
			</section>
		);
	}
}

const MortgageInformationCardWithProperty = withProperty(
	MortgageInformationCard
);

MortgageInformationCard.contextType = AuthContext;

export default withPropertyService(
	/** @type {React.ComponentType<any>} */ (
		MortgageInformationCardWithProperty
	)
);
